import type { TFunction } from 'i18next';

import type {
  BlackbaudRaisersEdgeIntegration,
  IBlackbaudRaisersEdgeIntegrationAttributes,
} from '@feathr/blackbox';
import { firstErrorMessage, submitModelWithErrorHandling, toast } from '@feathr/components';

type TIntegrationAttributes = Partial<IBlackbaudRaisersEdgeIntegrationAttributes>;

interface IPatchRaisersEdgeSettingArgs {
  changes: TIntegrationAttributes;
  integration: BlackbaudRaisersEdgeIntegration;
  successMessage: string;
  t: TFunction;
}

function pickAttributes(source: TIntegrationAttributes, keys: string[]): TIntegrationAttributes {
  return Object.fromEntries(
    keys.map((key) => [key, source[key as keyof TIntegrationAttributes]]),
  ) as TIntegrationAttributes;
}

/**
 * Saves a sync setting and toasts the result. Only the changed keys are sent, and on failure only
 * those keys go back to their values from before the call, so other unsaved edits stay untouched.
 */
export async function patchRaisersEdgeSetting({
  changes,
  integration,
  successMessage,
  t,
}: IPatchRaisersEdgeSettingArgs): Promise<boolean> {
  const changedKeys = integration.diff(changes);
  // Collection.patch sends nothing for an empty patch, so the model would report a stale error.
  if (!changedKeys.length) {
    toast(successMessage, { type: 'success' });
    return true;
  }

  const patch = pickAttributes(changes, changedKeys);
  const savedValues = pickAttributes(integration.toJS(), changedKeys);
  integration.set(patch);
  const isPatched = await submitModelWithErrorHandling({
    operation: integration.patch(patch),
    onError: (error) => {
      integration.set(savedValues);
      integration.setAttributeClean(...changedKeys);
      toast(t("Couldn't update setting. {{- error}}", { error: firstErrorMessage(error, t) }), {
        type: 'error',
      });
    },
  });
  if (isPatched) {
    toast(successMessage, { type: 'success' });
  }
  return isPatched;
}
